import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Project } from "../lib/api";
import { LoadingButton } from "./Spinner";
import { useToast } from "./Toast";
import { friendlyError } from "../lib/errors";
import { useConfirm } from "./ConfirmDialog";
import RelativeTime from "./RelativeTime";

interface Props {
  project: Project;
  open: boolean;
  onClose: () => void;
}

// Preset color themes — must match PALETTES in apps/api/src/lib/docs-portal.ts.
const PALETTES: Array<{ id: string; label: string; swatch: string }> = [
  { id: "slate", label: "Slate", swatch: "#0e1015" },
  { id: "midnight", label: "Midnight", swatch: "#000000" },
  { id: "zinc", label: "Zinc", swatch: "#161617" },
  { id: "warm", label: "Warm", swatch: "#1a1614" },
  { id: "ocean", label: "Ocean", swatch: "#0c121c" },
];

// Publish + manage a project's shareable documentation portal:
// enable, brand it (title / tagline / accent / default theme), choose
// public vs invite-only visibility, and manage viewer invites.
export default function DocsSiteModal({ project, open, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const key = ["docs-site", project.id];
  const siteQ = useQuery({ queryKey: key, queryFn: () => api.docsSites.get(project.id), enabled: open });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const create = useMutation({
    mutationFn: () => api.docsSites.create(project.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Documentation portal created"); },
    onError: (e) => toast.error(friendlyError(e)),
  });

  if (!open) return null;
  const site = siteQ.data?.site ?? null;
  const url = siteQ.data?.url ?? "";
  const viewers = siteQ.data?.viewers ?? [];

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center p-4 bg-[color:var(--backdrop)]" onClick={onClose}>
      <div className="card w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-[var(--shadow-strong)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <div>
            <h2 className="text-base font-semibold">Documentation portal</h2>
            <p className="text-xs muted-strong">A shareable, branded docs site for the {project.name} project</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2">×</button>
        </div>

        <div className="flex-1 min-h-0 overflow-auto scroll-pane p-5 space-y-5">
          {siteQ.isLoading ? (
            <div className="muted text-sm">Loading…</div>
          ) : !site ? (
            <div className="card p-5 text-center space-y-3">
              <div className="mx-auto h-12 w-12 grid place-items-center rounded-xl bg-accent-soft text-accent">
                <DocIcon />
              </div>
              <h3 className="font-semibold">Publish your docs</h3>
              <p className="text-sm muted max-w-sm mx-auto">
                Turn every docs block in this project — collections, requests, flows — into a clean,
                navigable site you can share with a link. You control who can see it.
              </p>
              <LoadingButton className="btn-primary" pending={create.isPending} onClick={() => create.mutate()}>
                Create documentation portal
              </LoadingButton>
            </div>
          ) : (
            <SiteEditor site={site} url={url} viewers={viewers} qcKey={key} confirm={confirm} toast={toast} />
          )}
        </div>
      </div>
    </div>
  );
}

function SiteEditor({ site, url, viewers, qcKey, confirm, toast }: {
  site: NonNullable<Awaited<ReturnType<typeof api.docsSites.get>>["site"]>;
  url: string;
  viewers: Array<{ id: string; email: string; last_viewed_at: string | null; created_at: string }>;
  qcKey: (string)[];
  confirm: ReturnType<typeof useConfirm>;
  toast: ReturnType<typeof useToast>;
}) {
  const qc = useQueryClient();
  // Local mirror so branding edits feel instant; saved on blur / button.
  const [title, setTitle] = useState(site.title ?? "");
  const [tagline, setTagline] = useState(site.tagline ?? "");
  const [accent, setAccent] = useState(site.accent ?? "#7c9cff");
  const [slug, setSlug] = useState(site.slug);
  const [inviteEmail, setInviteEmail] = useState("");

  useEffect(() => {
    setTitle(site.title ?? ""); setTagline(site.tagline ?? "");
    setAccent(site.accent ?? "#7c9cff"); setSlug(site.slug);
  }, [site.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = useMutation({
    mutationFn: (body: Parameters<typeof api.docsSites.update>[1]) => api.docsSites.update(site.id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qcKey }),
    onError: (e) => toast.error(friendlyError(e)),
  });
  const addViewer = useMutation({
    mutationFn: (email: string) => api.docsSites.addViewer(site.id, email),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: qcKey });
      setInviteEmail("");
      toast.success(r.emailed ? "Added · access email sent" : "Added to the allowlist");
    },
    onError: (e) => toast.error(friendlyError(e)),
  });
  const removeViewer = useMutation({
    mutationFn: (id: string) => api.docsSites.removeViewer(site.id, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qcKey }),
  });

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Share link copied"); } catch { /* ignore */ }
  };
  const downloadOpenApi = async () => {
    try {
      const res = await fetch(api.docsSites.openapiUrl(site.project_id), { credentials: "include" });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "openapi.yaml";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("openapi.yaml downloaded");
    } catch { toast.error("Couldn't export OpenAPI"); }
  };
  const accentValid = useMemo(() => /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(accent), [accent]);

  return (
    <>
      {/* Status + share link */}
      <div className="card p-4 space-y-3">
        <div className="flex items-center gap-3">
          <span className={"h-2.5 w-2.5 rounded-full shrink-0 " + (site.enabled ? "bg-good" : "bg-text-faint")} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">{site.enabled ? "Published" : "Unpublished"}</div>
            <div className="text-xs muted-strong">{site.enabled ? "Live at the link below" : "Hidden — turn on to share"}</div>
          </div>
          <label className="flex items-center gap-2 text-xs muted-strong">
            <input type="checkbox" checked={site.enabled} onChange={(e) => update.mutate({ enabled: e.target.checked })} />
            Published
          </label>
        </div>
        <div className="flex items-center gap-2">
          <input className="input input-mono flex-1 !text-xs" readOnly value={url} onFocus={(e) => e.target.select()} />
          <button type="button" className="btn text-xs" onClick={copyLink}>Copy</button>
          <a className="btn text-xs" href={url} target="_blank" rel="noopener noreferrer">Open ↗</a>
        </div>
        <div className="flex items-center gap-2 pt-1 border-t border-border">
          <span className="text-xs muted flex-1">Export the API surface as an OpenAPI 3.1 spec.</span>
          <button type="button" className="btn text-xs" onClick={downloadOpenApi}>Download openapi.yaml</button>
        </div>
      </div>

      {/* Visibility */}
      <div className="card p-4 space-y-2">
        <div className="label !m-0">Who can view</div>
        {([
          { v: "invite" as const, label: "Email verification", hint: "Readers enter their email and a one-time passcode to get in." },
          { v: "public" as const, label: "Anyone with the link", hint: "No sign-in — anyone with the URL can read it." },
        ]).map(({ v, label, hint }) => (
          <label key={v} className="flex items-start gap-2.5 rounded-lg border border-border p-2.5 cursor-pointer hover:bg-surface-2">
            <input type="radio" className="mt-0.5" checked={site.visibility === v} onChange={() => update.mutate({ visibility: v })} />
            <span>
              <span className="block text-sm font-medium">{label}</span>
              <span className="block text-xs muted">{hint}</span>
            </span>
          </label>
        ))}
        {site.visibility === "invite" && (
          <label className="flex items-start gap-2.5 rounded-lg border border-border p-2.5 cursor-pointer hover:bg-surface-2 mt-1">
            <input type="checkbox" className="mt-0.5" checked={site.open_signup} onChange={(e) => update.mutate({ open_signup: e.target.checked })} />
            <span>
              <span className="block text-sm font-medium">Let anyone request a passcode</span>
              <span className="block text-xs muted">On: any email can verify and read. Off: only emails on your allowlist below.</span>
            </span>
          </label>
        )}
      </div>

      {/* Content options */}
      <div className="card p-4 space-y-2">
        <div className="label !m-0">Content</div>
        <label className="flex items-start gap-2.5 text-sm cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={site.show_request_details} onChange={(e) => update.mutate({ show_request_details: e.target.checked })} />
          <span>Show request details <span className="muted text-xs">— headers, query params, and body for every request. Off shows just the prose docs.</span></span>
        </label>
        <label className="flex items-start gap-2.5 text-sm cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={site.show_branding} onChange={(e) => update.mutate({ show_branding: e.target.checked })} />
          <span>Show "Powered by API Stash" <span className="muted text-xs">— a small credit in the footer.</span></span>
        </label>
      </div>

      {/* Branding */}
      <div className="card p-4 space-y-3">
        <div className="label !m-0">Branding</div>
        <Field label="Title">
          <input className="input w-full" value={title} placeholder="My API docs"
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => title !== (site.title ?? "") && update.mutate({ title: title || null })} />
        </Field>
        <Field label="Tagline">
          <input className="input w-full" value={tagline} placeholder="Everything you need to integrate."
            onChange={(e) => setTagline(e.target.value)}
            onBlur={() => tagline !== (site.tagline ?? "") && update.mutate({ tagline: tagline || null })} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Accent color">
            <div className="flex items-center gap-2">
              <input type="color" className="h-9 w-10 rounded border border-border bg-surface cursor-pointer"
                value={accentValid ? accent : "#7c9cff"}
                onChange={(e) => { setAccent(e.target.value); update.mutate({ accent: e.target.value }); }} />
              <input className={"input input-mono flex-1 !text-xs " + (accentValid ? "" : "!border-danger")}
                value={accent} onChange={(e) => setAccent(e.target.value)}
                onBlur={() => accentValid && accent !== (site.accent ?? "") && update.mutate({ accent })} />
            </div>
          </Field>
          <Field label="Default theme">
            <select className="input w-full" value={site.theme} onChange={(e) => update.mutate({ theme: e.target.value as "dark" | "light" })}>
              <option value="dark">Dark</option>
              <option value="light">Light</option>
            </select>
          </Field>
        </div>
        <Field label="Color theme">
          <div className="flex flex-wrap gap-2">
            {PALETTES.map((p) => (
              <button
                key={p.id}
                type="button"
                title={p.label}
                aria-label={p.label}
                onClick={() => update.mutate({ palette: p.id })}
                className={"h-8 w-8 rounded-lg border-2 transition " + (site.palette === p.id ? "border-accent scale-105" : "border-border hover:border-border-strong")}
                style={{ background: p.swatch }}
              />
            ))}
          </div>
        </Field>
        <Field label="Link slug">
          <div className="flex items-center gap-1.5">
            <span className="text-xs muted-strong mono shrink-0">/d/</span>
            <input className="input input-mono flex-1 !text-xs" value={slug}
              onChange={(e) => setSlug(e.target.value)}
              onBlur={() => slug !== site.slug && update.mutate({ slug })} />
          </div>
        </Field>
      </div>

      {/* Allowlist (invite-only, when not open to anyone) */}
      {site.visibility === "invite" && (
        <div className="card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="label !m-0">{site.open_signup ? "Who's viewed" : "Allowed viewers"}</div>
              <div className="text-xs muted">{site.open_signup
                ? "Anyone can request a passcode; people who've verified show up here."
                : "Only these emails can request a passcode. They verify with a code we email them."}</div>
            </div>
            <span className="text-xs muted-strong shrink-0">{viewers.length}</span>
          </div>
          {!site.open_signup && (
            <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (inviteEmail.trim()) addViewer.mutate(inviteEmail.trim()); }}>
              <input className="input flex-1" type="email" placeholder="teammate@example.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
              <LoadingButton type="submit" className="btn-primary" pending={addViewer.isPending}>Add</LoadingButton>
            </form>
          )}
          <ul className="space-y-1.5">
            {viewers.length === 0 && (
              <li className="text-xs muted italic">
                {site.open_signup ? "No one has verified yet." : "No one added yet. Add an email — they'll get a passcode when they open the docs."}
              </li>
            )}
            {viewers.map((v) => (
              <li key={v.id} className="flex items-center gap-2 rounded border border-border px-3 py-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{v.email}</div>
                  <div className="text-xs muted-strong">
                    {v.last_viewed_at ? <>last viewed <RelativeTime iso={v.last_viewed_at} /></> : "not viewed yet"}
                  </div>
                </div>
                <button type="button" className="btn text-xs" aria-label="Remove access"
                  onClick={async () => { if (await confirm({ title: `Remove access for ${v.email}?`, danger: true })) removeViewer.mutate(v.id); }}>Remove</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

function DocIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M13 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10z" /><path d="M13 3v7h7" /><path d="M8 14h8M8 18h5" />
    </svg>
  );
}
